import { useEffect, useState } from 'react'
import { Save, RotateCcw, AlertTriangle, TrendingUp, Radio, Scale } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, RefreshButton } from '../ui'
import { get, put } from '../../lib/api'
import { toast } from '../../store'
import { clsx, fa, num } from '../../lib/format'

export default function SettingsPage() {
  const [groups, setGroups] = useState<any[] | null>(null)
  const [values, setValues] = useState<Record<string, any>>({})
  const [dirty, setDirty] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    get('/admin/system/settings').then((r: any) => {
      setGroups(r.groups)
      const v: Record<string, any> = {}
      r.groups.forEach((g: any) => g.items.forEach((i: any) => (v[i.key] = i.value)))
      setValues(v)
      setDirty(new Set())
    }).catch((e) => toast.err(e.message)).finally(() => setLoading(false))
  }
  const [rate, setRate] = useState<any>(null)
  const [expo, setExpo] = useState<any>(null)
  const loadRate = () => get('/admin/system/rate-status').then(setRate).catch(() => setRate(null))
  // ریسکِ اهرم هم هر ۱۵ ثانیه تازه می‌شود: طرفِ این قراردادها خودِ صرافی
  // است، پس مدیر باید بتواند لحظه‌ای ببیند چقدر در معرض بازار است.
  const loadExpo = () => get('/admin/system/leverage-exposure').then(setExpo).catch(() => setExpo(null))
  useEffect(() => {
    load(); loadRate(); loadExpo()
    const id = setInterval(() => { loadRate(); loadExpo() }, 15000)
    return () => clearInterval(id)
  }, [])

  const set = (key: string, val: any) => {
    setValues((s) => ({ ...s, [key]: val }))
    setDirty((s) => new Set(s).add(key))
  }

  const save = async () => {
    if (!dirty.size) return
    setSaving(true)
    try {
      const payload: Record<string, any> = {}
      dirty.forEach((k) => (payload[k] = values[k]))
      const r: any = await put('/admin/system/settings', { values: payload })
      toast.ok(`${fa(r.changed.length)} تنظیم ذخیره شد`)
      load()
    } catch (e: any) { toast.err(e.message) } finally { setSaving(false) }
  }

  return (
    <>
      <PageHead title="تنظیمات سامانه" subtitle="کنترل رفتار کل سایت — تغییرات بلافاصله اعمال می‌شوند"
        action={
          <div className="flex gap-2">
            <RefreshButton onClick={load} loading={loading} />
            <button onClick={save} disabled={!dirty.size || saving} className="btn-primary !py-2 text-xs">
              <Save className="h-3.5 w-3.5" /> ذخیره {dirty.size > 0 && `(${fa(dirty.size)})`}
            </button>
          </div>
        } />

      {values.maintenance_mode && (
        <div className="mb-5 flex items-start gap-2 rounded-2xl border border-down/25 bg-down/[.07] p-4 text-xs leading-6 text-down">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          حالت تعمیرات فعال است؛ کاربران نمی‌توانند معامله، واریز یا برداشت کنند.
        </div>
      )}

      {rate && <RatePanel rate={rate} />}
      {expo?.enabled && <ExposurePanel e={expo} />}

      {groups === null ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-40 w-full" />)}</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {groups.map((g) => (
            <Panel key={g.group} title={g.group}>
              <div className="space-y-4">
                {g.items.map((it: any) => (
                  <Field key={it.key} item={it} value={values[it.key]}
                    changed={dirty.has(it.key)} onChange={(v) => set(it.key, v)} />
                ))}
              </div>
            </Panel>
          ))}
        </div>
      )}
    </>
  )
}

function Field({ item, value, onChange, changed }:
  { item: any; value: any; onChange: (v: any) => void; changed: boolean }) {
  const isBool = item.kind === 'bool'
  const isNum = item.kind === 'int' || item.kind === 'float'
  const isSecret = item.kind === 'secret'

  return (
    <div className={clsx('rounded-xl border p-3 transition',
      changed ? 'border-brand-500/40 bg-brand-500/[.06]' : 'border-white/[.06] bg-white/[.02]')}>
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium text-slate-200">{item.label || item.key}</div>
          {item.hint && <div className="mt-0.5 text-[10px] leading-5 text-slate-500">{item.hint}</div>}
        </div>
        {isBool ? (
          <button onClick={() => onChange(!value)}
            className={clsx('relative h-6 w-11 shrink-0 rounded-full transition',
              value ? 'bg-gradient-to-l from-brand-600 to-brand-400' : 'bg-white/10')}>
            <span className={clsx('absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all',
              value ? 'right-0.5' : 'right-[1.375rem]')} />
          </button>
        ) : null}
      </div>

      {!isBool && (
        <input
          className="input num !py-2 text-xs"
          type={isSecret ? 'password' : 'text'}
          dir={isNum || isSecret ? 'ltr' : 'rtl'}
          placeholder={isSecret ? (item.masked || 'مقدار محرمانه ثبت نشده است') : undefined}
          value={value ?? ''}
          onChange={(e) => onChange(isNum ? e.target.value.replace(/[^\d.\-]/g, '') : e.target.value)}
        />
      )}

      {isSecret && (
        <div className="mt-1 text-[10px] leading-5 text-slate-500">
          {item.isSet ? 'مقدار فعلی ثبت شده است — برای حفظ آن این فیلد را خالی بگذارید.'
            : 'هنوز مقداری ثبت نشده است.'}
        </div>
      )}

      {isNum && Number(value) >= 1000 && (
        <div className="num mt-1 text-[10px] text-slate-500">{num(Number(value))}</div>
      )}
      <div className="num mt-1 text-[10px] text-slate-600" dir="ltr">{item.key}</div>
    </div>
  )
}


/* --------------------------------------------------- وضعیت زنده نرخ دلار */
function RatePanel({ rate }: { rate: any }) {
  const manual = rate.mode === 'manual'
  const drift = Math.abs(rate.diffPercent ?? 0)
  // اختلاف زیاد بین نرخ سایت و نرخ بازار تقریباً همیشه یعنی یک نرخ دستیِ
  // فراموش‌شده؛ بی‌صدا ماندنش یعنی روزها قیمت غلط به کاربر نشان دادن.
  const warn = drift >= 3 || rate.stale

  return (
    <div className={clsx('mb-5 rounded-2xl border p-4',
      warn ? 'border-gold/30 bg-gold/[.06]' : 'border-white/[.07] bg-white/[.03]')}>
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-slate-200">
        <TrendingUp className="h-4 w-4" /> نرخ دلار سایت
        <span className={clsx('chip !py-0.5 text-[10px]',
          rate.stale ? 'border-down/30 text-down' : 'border-up/30 text-up')}>
          <Radio className="h-3 w-3" />
          {rate.source === 'nobitex' ? 'نوبیتکس (زنده)'
            : rate.source === 'binance' ? 'بایننس'
            : 'شبیه‌ساز — قیمت واقعی نیست'}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="نرخ بازار (نوبیتکس)" value={`${num(rate.marketRate)} تومان`} />
        <Stat label="فروش به کاربر" value={`${num(rate.siteRate)} تومان`} strong />
        <Stat label="خرید از کاربر" value={`${num(rate.buyRate ?? 0)} تومان`} strong />
        <Stat label="اسپرد (سود ناخالص)"
          value={`${num(rate.spreadPercent ?? 0, 2)}٪`}
          tone={drift >= 3 ? 'warn' : undefined} />
      </div>

      {(rate.margin || rate.buyDiscount) ? (
        <div className="mt-3 rounded-xl border border-brand-500/25 bg-brand-500/[.06] p-3">
          <div className="mb-2 space-y-1 text-[11px] text-brand-200">
            <div>
              فروش به کاربر: <b>{num(rate.margin, 2)}٪ {rate.margin >= 0 ? 'بالاتر' : 'پایین‌تر'}</b> از نوبیتکس
            </div>
            <div>
              خرید از کاربر: <b>{num(rate.buyDiscount ?? 0, 2)}٪ پایین‌تر</b> از نوبیتکس
              {!rate.buyDiscount && <span className="text-slate-400"> — یعنی دقیقاً به نرخ نوبیتکس می‌خرید</span>}
            </div>
          </div>
          {(rate.sample ?? []).length > 0 && (
            <table className="w-full text-[10px]">
              <thead className="text-slate-500">
                <tr><th className="text-right font-normal">ارز</th>
                  <th className="text-left font-normal">نوبیتکس</th>
                  <th className="text-left font-normal">خرید ما</th>
                  <th className="text-left font-normal">فروش ما</th></tr>
              </thead>
              <tbody className="num">
                {rate.sample.map((r: any) => (
                  <tr key={r.symbol}>
                    <td className="py-0.5 text-right text-slate-400">{r.symbol}</td>
                    <td className="py-0.5 text-left text-slate-400">{num(r.market)}</td>
                    <td className="py-0.5 text-left font-semibold text-up">{num(r.buy)}</td>
                    <td className="py-0.5 text-left font-semibold text-white">{num(r.site)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      <div className="mt-3 text-[10px] leading-6 text-slate-500">
        {manual
          ? <>نرخ دلار <b className="text-gold">دستی</b> است: قیمت‌های تومانی از روی
              نرخ ثابت {num(rate.manual)} تومان ساخته می‌شوند و نرخ نوبیتکس نادیده گرفته می‌شود.</>
          : <>نرخ دلار <b className="text-up">خودکار</b> از نوبیتکس گرفته می‌شود.</>}
        {rate.stale && <> <b className="text-down">اتصال به نوبیتکس برقرار نیست؛ قیمت‌ها شبیه‌سازی‌شده‌اند.</b></>}
      </div>
    </div>
  )
}

function ExposurePanel({ e }: { e: any }) {
  // اختلاف دو سمت، همان چیزی است که صرافی واقعاً رویش ریسک دارد؛ اگر خرید و
  // فروش برابر باشند، حرکت بازار برای صرافی خنثی است و فقط کارمزد می‌ماند.
  const net = Math.abs(e.netIrt ?? 0)
  const warn = e.usedPct >= 70 || (e.totalIrt > 0 && net / e.totalIrt > 0.6)
  return (
    <div className={clsx('mb-5 rounded-2xl border p-4',
      warn ? 'border-gold/30 bg-gold/[.06]' : 'border-white/[.07] bg-white/[.03]')}>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-200">
        <Scale className="h-4 w-4" /> ریسک باز معاملات اهرمی
        <span className="chip !py-0.5 text-[10px] border-white/15 text-slate-400">
          {fa(e.positions)} موقعیت · {fa(e.users)} کاربر
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="مجموع خرید (Long)" value={`${num(e.longIrt)} تومان`} />
        <Stat label="مجموع فروش (Short)" value={`${num(e.shortIrt)} تومان`} />
        <Stat label="اختلاف دو سمت — ریسک واقعی" value={`${num(e.netIrt)} تومان`}
          strong tone={warn ? 'warn' : undefined} />
        <Stat label="وثیقه در دست صرافی" value={`${num(e.marginIrt)} تومان`} />
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Stat label="سود/زیان تحقق‌یافته صرافی" value={`${num(e.houseIrt)} تومان`} strong />
        <Stat label="کارمزد دریافتی" value={`${num(e.feesIrt)} تومان`} />
        <Stat label="لیکویید شده" value={fa(e.liquidations)} />
      </div>
      {e.capIrt > 0 && (
        <div className="mt-3">
          <div className="mb-1 flex justify-between text-[10px] text-slate-500">
            <span>مصرف از سقف کل صرافی</span>
            <span className="num">{num(e.usedPct, 1)}٪</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div className={clsx('h-full rounded-full', e.usedPct >= 70 ? 'bg-gold' : 'bg-brand-500')}
              style={{ width: `${Math.min(100, e.usedPct)}%` }} />
          </div>
        </div>
      )}
      <p className="mt-3 text-[11px] leading-6 text-slate-500">
        در این مدل طرفِ قرارداد خودِ صرافی است: سود کاربر از جیب صرافی می‌رود.
        اگر «اختلاف دو سمت» بزرگ شود، صرافی عملاً روی جهت بازار شرط بسته است.
      </p>
    </div>
  )
}

function Stat({ label, value, strong, tone }:
  { label: string; value: string; strong?: boolean; tone?: 'warn' }) {
  return (
    <div className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
      <div className="text-[10px] text-slate-500">{label}</div>
      <div className={clsx('num mt-1 text-sm',
        tone === 'warn' ? 'text-gold' : strong ? 'font-bold text-white' : 'text-slate-200')}>
        {value}
      </div>
    </div>
  )
}
